import { CanvasTextMetrics, Container, Graphics, Rectangle, type Texture } from 'pixi.js';
import { Scene } from '../app/SceneManager';
import { COLORS, DESIGN_W } from '../app/theme';
import { Button, makeText, avatarSprite, sceneBackground, iconSprite, glassCard } from '../ui/widgets';
import { enterFade, enterPop } from './common';
import { pulse } from '../app/anim';
import { viewport } from '../app/viewport';
import type { AppContext } from '../app/AppContext';
import { GameName, RoomName } from '../net/enums';
import { MessageTag } from '../net/messageTags';
import { gameState, type AppState } from '../state/GameState';
import { encodeStatus, decodeMainMenu, encodeReadMessage, encodeTelegramRequest, decodeTelegram } from '../net/codec/lobby';
import { encodeCancelGame } from '../net/codec/matchmaking';
import { openExternalUrl } from '../platform/telegram';
import { moneyFa, normalizeRtlAnnouncement, rtlIsolateLines, toPersianDigits } from '../util/persian';
import { mainMenuSystemMessageLayout } from './mainMenuLayout';

/**
 * The lobby hub (port of MainMenuCode.cs), re-skinned to the glass-card style: a unified header card
 * (brand logo + tappable avatar + plain-text username/balance/online — no nested pill frames), an admin
 * "system message" glass strip, a «انتخاب بازی» card with three game-mode tiles (Hokm + Blackjack active
 * → their create flow; Backgammon greyed with a «به‌زودی» badge), and the Search/Dashboard/Profile/back
 * navigation. A player who holds a Wait table sees a dedicated, game-agnostic waiting panel in place
 * of the create choices while Search remains available for watching or joining another table.
 */
export class MainMenuScene extends Scene {
  private nameText!: ReturnType<typeof makeText>;
  private balanceText!: ReturnType<typeof makeText>;
  private onlineText!: ReturnType<typeof makeText>;
  private adminText!: ReturnType<typeof makeText>;
  private systemStrip!: Container;
  private systemCard!: Container;
  private systemAdminIcon!: Container;
  private systemAdminCaption!: ReturnType<typeof makeText>;
  private systemOkButton!: Button;
  private modeSection!: Container;
  private gameChoiceContent!: Container;
  private waitingPanel!: Container;
  private searchButton!: Button;
  private avatarHolder!: Container;
  private header!: Container;
  private currentAvatar = -1;
  private currentBalance = -1n;
  private built = false;

  constructor(private readonly ctx: AppContext) {
    super();
  }

  onEnter(): void {
    this.layout();

    this.track(gameState.subscribe((s) => this.render(s)));
    // Refresh lobby state from the server (also updates isWaiting / admin message).
    this.track(this.ctx.client.on(MessageTag.MainMenu, (r) => gameState.applyMainMenu(decodeMainMenu(r))));
    // Telegram(6): the channel button asks the server for the URL; the reply carries it (port of MainMenuCode.cs).
    this.track(this.ctx.client.on(MessageTag.Telegram, (r) => openExternalUrl(decodeTelegram(r))));
    this.ctx.client.send(encodeStatus());

    this.render(gameState.get());
  }

  /** Re-flow on viewport-height change (Telegram fullscreen / rotation). */
  onResize(): void {
    this.layout();
    this.render(gameState.get());
  }

  /** Build (or rebuild) all visuals against the live height. Subscriptions stay in onEnter. */
  private layout(): void {
    this.currentAvatar = -1; // force the avatar to re-attach after a rebuild
    for (const ch of this.removeChildren()) ch.destroy({ children: true });

    this.addChild(sceneBackground());
    this.buildTopChrome();
    this.buildSystemStrip();
    const tiles = this.buildModeStrip();
    const nav = this.buildNav();

    // Subtle staggered entrance: header fades, mode tiles pop in, nav buttons fade up — first build only.
    if (!this.built) {
      this.built = true;
      enterFade([this.header]);
      enterPop(tiles, 70);
      enterFade(nav, 55);
    }
  }

  private buildTopChrome(): void {
    // Top-left utility icons (channel + support), above the header card. The channel button asks the
    // server for the URL (Telegram message) and opens the reply — same flow as Unity's MainMenuCode.
    this.cornerIcon('telegram', 'کانال', 22, 18, () => this.ctx.client.send(encodeTelegramRequest()));
    this.cornerIcon('support', 'پشتیبانی', 102, 18, () => this.ctx.navigate(RoomName.Ticket));

    // Unified identity header card: brand logo (left), tappable avatar (right), and plain-text
    // username / balance / online rendered directly on the card (no nested info-pill frames).
    const cardX = 24;
    const cardY = 104;
    const cardW = DESIGN_W - 48;
    const cardH = 150;
    const header = new Container();
    this.header = header;
    header.addChild(glassCard(cardX, cardY, cardW, cardH));
    this.addChild(header);

    const logo = iconSprite(this.ctx.iconTexture('logo'), 92);
    logo.position.set(cardX + 24, cardY + (cardH - 92) / 2);
    header.addChild(logo);

    const avSize = 100;
    const avX = cardX + cardW - 20 - avSize;
    const avY = cardY + (cardH - avSize) / 2;
    this.avatarHolder = new Container();
    this.avatarHolder.position.set(avX, avY);
    this.avatarHolder.eventMode = 'static';
    this.avatarHolder.cursor = 'pointer';
    this.avatarHolder.hitArea = new Rectangle(0, 0, avSize, avSize);
    this.avatarHolder.on('pointertap', () => this.ctx.navigate(RoomName.Profile));
    header.addChild(this.avatarHolder);

    const rightX = avX - 18;
    this.nameText = makeText(gameState.get().username || '—', { size: 30, weight: '700', fill: COLORS.text, align: 'right' });
    this.nameText.anchor.set(1, 0);
    this.nameText.position.set(rightX, cardY + 22);
    header.addChild(this.nameText);

    this.balanceText = this.balanceHeaderRow(rightX, cardY + 66);
    this.onlineText = this.headerRow(rightX, cardY + 104, 'users', 'آنلاین: —', COLORS.textDim, () => this.ctx.navigate(RoomName.OnlineList));
  }

  /**
   * Keep the RTL label and LTR money token in separate Pixi text nodes. Canvas bidi handling can
   * otherwise move a Latin magnitude suffix (M/K/…) across «موجودی» even when Unicode isolates are
   * present in the combined string.
   */
  private balanceHeaderRow(rightX: number, y: number): ReturnType<typeof makeText> {
    const iconSize = 26;
    const gap = 8;
    const textRight = rightX - iconSize - gap;

    const label = makeText('موجودی:', { size: 26, weight: '700', fill: COLORS.gold, align: 'right' });
    label.anchor.set(1, 0);
    label.position.set(textRight, y);
    this.header.addChild(label);

    const value = makeText('—', { size: 26, weight: '700', fill: COLORS.gold, align: 'right' });
    value.anchor.set(1, 0);
    value.position.set(textRight - label.width - gap, y);
    this.header.addChild(value);

    const icon = iconSprite(this.ctx.iconTexture('coin'), iconSize);
    icon.position.set(rightX - iconSize, y - 1);
    this.header.addChild(icon);

    const zone = new Container();
    zone.eventMode = 'static';
    zone.cursor = 'pointer';
    zone.hitArea = new Rectangle(rightX - 320, y - 12, 320, 54);
    zone.on('pointertap', () => this.ctx.navigate(RoomName.Payment));
    this.header.addChild(zone);
    return value;
  }

  /** A right-aligned «text + leading icon» row on the header card, with a tap zone covering it. */
  private headerRow(rightX: number, y: number, iconName: string, text: string, color: number, onTap: () => void): ReturnType<typeof makeText> {
    const iconSize = 26;
    const gap = 8;
    const t = makeText(text, { size: 26, weight: '700', fill: color, align: 'right' });
    t.anchor.set(1, 0);
    t.position.set(rightX - iconSize - gap, y);
    this.header.addChild(t);
    const icon = iconSprite(this.ctx.iconTexture(iconName), iconSize);
    icon.position.set(rightX - iconSize, y - 1);
    this.header.addChild(icon);
    const zone = new Container();
    zone.eventMode = 'static';
    zone.cursor = 'pointer';
    zone.hitArea = new Rectangle(rightX - 320, y - 12, 320, 54);
    zone.on('pointertap', onTap);
    this.header.addChild(zone);
    return t;
  }

  /** A small top-left icon button with a caption beneath it. */
  private cornerIcon(icon: string, caption: string, x: number, y: number, onTap: () => void): void {
    const size = 60;
    const holder = iconSprite(this.ctx.iconTexture(icon), size);
    holder.position.set(x, y);
    holder.eventMode = 'static';
    holder.cursor = 'pointer';
    holder.hitArea = new Rectangle(-10, -10, 82, 82);
    holder.on('pointertap', onTap);
    holder.on('pointerover', () => (holder.alpha = 0.75));
    holder.on('pointerout', () => (holder.alpha = 1));
    this.addChild(holder);
    const lbl = makeText(caption, { size: 20, fill: COLORS.textDim, align: 'center' });
    lbl.anchor.set(0.5, 0);
    lbl.position.set(x + size / 2, y + size + 2);
    this.addChild(lbl);
  }

  private buildSystemStrip(): void {
    const w = DESIGN_W - 48;
    this.systemStrip = new Container();
    this.systemStrip.position.set(24, 274);
    this.addChild(this.systemStrip);

    this.systemCard = new Container();
    this.systemStrip.addChild(this.systemCard);

    // Keep the announcement as one RTL card: a compact identity row, full-width message body, and
    // a separate acknowledgement footer. This reads naturally instead of squeezing three columns
    // into the same two lines of text.
    this.systemAdminIcon = new Container();
    this.systemAdminIcon.position.x = w - 64;
    const announcer = iconSprite(
      this.ctx.iconTexture('system-announcer-v2') ?? this.ctx.respackFrame('boss (2)'),
      52,
    );
    this.systemAdminIcon.addChild(announcer);
    this.systemStrip.addChild(this.systemAdminIcon);
    this.systemAdminCaption = makeText('پیام سیستم', {
      size: 21,
      weight: '700',
      fill: COLORS.gold,
      align: 'right',
    });
    this.systemAdminCaption.anchor.set(1, 0.5);
    this.systemAdminCaption.position.x = w - 76;
    this.systemStrip.addChild(this.systemAdminCaption);

    this.adminText = makeText('', { size: 24, fill: COLORS.text, align: 'right' });
    this.adminText.anchor.set(1, 0);
    this.adminText.style.wordWrap = true;
    this.systemStrip.addChild(this.adminText);

    // «باشه» acknowledges the message to the server via ReadMessage(7).
    this.systemOkButton = new Button(
      'باشه',
      () => {
        this.ctx.client.send(encodeReadMessage());
        gameState.update({ adminMessage: '' });
      },
      { width: 98, height: 46, fontSize: 20, radius: 14 },
    );
    this.systemOkButton.position.x = 20;
    this.systemStrip.addChild(this.systemOkButton);
  }

  /** Thumb-friendly actions anchored above the Telegram safe-area. */
  private navYs(): { rowY: number } {
    const rowY = viewport.h - 104;
    return { rowY };
  }

  private buildModeStrip(): Container[] {
    // iPhone portrait works better as a vertical decision list than three small desktop-style icons.
    // The whole section moves up when there is no system notice, eliminating the first dead band.
    const tiles: Container[] = [];
    const BLOCK = 562;
    const cardX = 24;
    const cardW = DESIGN_W - 48;
    this.modeSection = new Container();
    this.modeSection.position.set(0, 286);
    this.modeSection.addChild(glassCard(cardX, 0, cardW, BLOCK));
    this.addChild(this.modeSection);

    this.gameChoiceContent = new Container();
    this.modeSection.addChild(this.gameChoiceContent);

    const heading = makeText('چه بازی‌ای می‌خواهی؟', { size: 30, weight: '700', align: 'right', fill: COLORS.gold });
    heading.anchor.set(0.5, 0);
    heading.position.set(DESIGN_W / 2, 20);
    this.gameChoiceContent.addChild(heading);

    this.modeTile(this.ctx.gameIcon(GameName.Hokm), 'حکم', 'بازی کلاسیک دونفره', 72, () => this.ctx.navigate(RoomName.CreateHokm), tiles);
    this.modeTile(this.ctx.gameIcon(GameName.Backgammon), 'تخته نرد', 'رقابت دونفره سریع', 230, () => this.ctx.navigate(RoomName.CreateBackgammon), tiles);
    this.modeTile(this.ctx.gameIcon(GameName.Blackjack), 'بیست و یک', 'میز بساز و حریف پیدا کن', 388, () => this.ctx.navigate(RoomName.NewGame), tiles);

    this.waitingPanel = this.buildWaitingPanel();
    this.modeSection.addChild(this.waitingPanel);
    return tiles;
  }

  private buildWaitingPanel(): Container {
    const panel = new Container();
    panel.position.set(48, 72);
    panel.addChild(glassCard(0, 0, 624, 438, 22));

    const icon = iconSprite(this.ctx.iconTexture('search'), 92);
    icon.position.set(266, 28);
    panel.addChild(icon);

    const title = makeText('در انتظار حریف', { size: 34, weight: '700', align: 'center', fill: COLORS.gold });
    title.anchor.set(0.5, 0);
    title.position.set(312, 128);
    panel.addChild(title);

    const status = makeText('میز شما ساخته شده و در حال جستجوی حریف است.', { size: 24, align: 'center', fill: COLORS.text });
    status.anchor.set(0.5, 0);
    status.position.set(312, 184);
    panel.addChild(status);

    const note = makeText('تماشای بازی‌های دیگر مشکلی ندارد؛ با پیوستن به میز دیگری، این درخواست لغو می‌شود.', {
      size: 21,
      align: 'center',
      fill: COLORS.textDim,
    });
    note.anchor.set(0.5, 0);
    note.style.wordWrap = true;
    note.style.wordWrapWidth = 528;
    note.position.set(312, 240);
    panel.addChild(note);

    const cancel = new Button('لغو درخواست بازی', () => this.ctx.client.send(encodeCancelGame()), {
      width: 528,
      height: 84,
      fontSize: 27,
      fill: COLORS.danger,
      accessibleLabel: 'لغو درخواست بازی در انتظار',
    });
    cancel.position.set(48, 330);
    panel.addChild(cancel);
    return panel;
  }

  /** A large, one-tap game choice with enough copy to remove guesswork. */
  private modeTile(
    icon: Texture | undefined,
    caption: string,
    subtitle: string,
    top: number,
    onTap: () => void,
    tiles: Container[],
  ): Button {
    const button = new Button(caption, onTap, {
      width: DESIGN_W - 96,
      height: 138,
      fontSize: 32,
      fill: COLORS.panel,
      textColor: COLORS.text,
      icon: icon ?? null,
      accessibleLabel: `${caption}، ${subtitle}`,
    });
    button.position.set(48, top);
    const detail = makeText(subtitle, { size: 23, fill: COLORS.textDim, align: 'center' });
    detail.anchor.set(0.5, 0);
    detail.position.set((DESIGN_W - 96) / 2 - 28, 88);
    button.addChild(detail);
    this.gameChoiceContent.addChild(button);
    tiles.push(button);
    return button;
  }

  private buildNav(): Container[] {
    const w = DESIGN_W - 48;
    const { rowY } = this.navYs();

    const search = new Button('جستجو بازی', () => this.ctx.navigate(RoomName.SearchGame), {
      width: w,
      height: 92,
      fontSize: 30,
      icon: this.ctx.iconTexture('search') ?? null,
    });
    search.position.set(24, 866);
    this.addChild(search);
    this.searchButton = search;

    const bw = (w - 24) / 3;
    const home = new Button('خانه', () => {}, {
      width: bw,
      height: 88,
      fontSize: 24,
      fill: COLORS.panelSoft,
      textColor: COLORS.gold,
      icon: this.ctx.iconTexture('logo') ?? null,
      haptic: false,
    });
    home.position.set(24 + (bw + 12) * 2, rowY);
    this.addChild(home);
    const dashboard = new Button('کیف پول', () => this.ctx.navigate(RoomName.Payment), {
      width: bw,
      height: 88,
      fontSize: 24,
      icon: this.ctx.iconTexture('wallet') ?? null,
      fill: COLORS.panel,
      textColor: COLORS.text,
    });
    dashboard.position.set(24 + bw + 12, rowY);
    this.addChild(dashboard);
    const profile = new Button('پروفایل', () => this.ctx.navigate(RoomName.Profile), {
      width: bw,
      height: 88,
      fontSize: 24,
      icon: this.ctx.iconTexture('user') ?? null,
      fill: COLORS.panel,
      textColor: COLORS.text,
    });
    profile.position.set(24, rowY);
    this.addChild(profile);
    return [search, home, dashboard, profile];
  }

  private render(s: Readonly<AppState>): void {
    this.onlineText.text = `آنلاین: ${toPersianDigits(s.onlineCount)}`;
    this.nameText.text = s.username || '—';
    this.balanceText.text = moneyFa(s.balance);
    // Pulse the balance when it actually changes (e.g. after returning from the wallet).
    if (this.currentBalance !== -1n && s.balance !== this.currentBalance) pulse(this.balanceText, 1.05, 220);
    this.currentBalance = s.balance;

    const msg = normalizeRtlAnnouncement(s.adminMessage ?? '');
    const hasMessage = msg.length > 0;
    this.systemStrip.visible = hasMessage;

    // Establish the horizontal column before measuring text; measuring against Pixi's default wrap
    // width would make a short message look artificially tall and shrink it unnecessarily.
    const messageBounds = mainMenuSystemMessageLayout(hasMessage, 0);
    this.adminText.style.wordWrapWidth = messageBounds.messageWidth;
    this.adminText.position.x = messageBounds.messageCenterX + messageBounds.messageWidth / 2;

    // Ask Pixi for the logical wrap points, then isolate every resulting line as its own RTL
    // paragraph. A single isolate around the unwrapped block is not stable on iOS Safari because its
    // canvas bidi implementation resets direction after Pixi inserts the first newline.
    this.adminText.style.wordWrap = true;
    this.adminText.style.whiteSpace = 'normal';
    this.adminText.style.fontSize = 24;
    let measuredHeight = 0;
    let wrappedLines: string[] = [];
    while (hasMessage) {
      const metrics = CanvasTextMetrics.measureText(msg, this.adminText.style);
      measuredHeight = metrics.height;
      wrappedLines = metrics.lines;
      if (measuredHeight <= 76 || Number(this.adminText.style.fontSize) <= 18) break;
      this.adminText.style.fontSize = Number(this.adminText.style.fontSize) - 1;
    }
    this.adminText.style.wordWrap = false;
    this.adminText.text = hasMessage ? rtlIsolateLines(wrappedLines) : '';
    const finalSystemLayout = mainMenuSystemMessageLayout(hasMessage, measuredHeight);
    for (const child of this.systemCard.removeChildren()) child.destroy({ children: true });
    const stripWidth = DESIGN_W - 48;
    const card = glassCard(0, 0, stripWidth, finalSystemLayout.stripHeight);
    card.addChild(
      new Graphics()
        .moveTo(20, 54)
        .lineTo(stripWidth - 20, 54)
        .stroke({ color: COLORS.panelBorder, width: 1, alpha: 0.38 })
        .moveTo(132, finalSystemLayout.stripHeight - 35)
        .lineTo(stripWidth - 20, finalSystemLayout.stripHeight - 35)
        .stroke({ color: COLORS.panelBorder, width: 1, alpha: 0.22 }),
    );
    this.systemCard.addChild(card);
    this.adminText.position.y = finalSystemLayout.messageTop;
    this.systemAdminIcon.position.y = 2;
    this.systemAdminCaption.position.y = 28;
    this.systemOkButton.position.y = finalSystemLayout.stripHeight - 58;
    this.modeSection.position.y = finalSystemLayout.modeSectionY;
    this.searchButton.position.y = finalSystemLayout.searchButtonY;

    this.gameChoiceContent.visible = !s.isWaiting;
    this.waitingPanel.visible = s.isWaiting;

    if (s.avatar !== this.currentAvatar) {
      this.currentAvatar = s.avatar;
      this.avatarHolder.removeChildren();
      this.avatarHolder.addChild(avatarSprite(this.ctx.profileAtlas, s.avatar, 100));
    }
  }
}
